
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";

const techstarsSchema = z.object({
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  contactEmail: z.string().email("Please enter a valid email address"),
  batchCohort: z.string().min(1, "Please select your Techstars batch"),
  companyDescription: z.string().min(10, "Please provide at least 10 characters"),
});

const otherSchema = z.object({
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  contactEmail: z.string().email("Please enter a valid email address"),
  acceleratorName: z.string().min(2, "Please enter your accelerator name"),
  batchCohort: z.string().optional(),
  companyDescription: z.string().min(10, "Please provide at least 10 characters"),
  verificationDetails: z.string().min(5, "Please provide verification details"),
});

type TechstarsFormData = z.infer<typeof techstarsSchema>;
type OtherFormData = z.infer<typeof otherSchema>;

interface AcceleratorApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  acceleratorType: "techstars" | "other";
}

const AcceleratorApplicationModal = ({
  isOpen,
  onClose,
  acceleratorType,
}: AcceleratorApplicationModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isTechstars = acceleratorType === "techstars";
  const schema = isTechstars ? techstarsSchema : otherSchema;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: isTechstars
      ? {
          companyName: "",
          contactEmail: "",
          batchCohort: "",
          companyDescription: "",
        }
      : {
          companyName: "",
          contactEmail: "",
          acceleratorName: "",
          batchCohort: "",
          companyDescription: "",
          verificationDetails: "",
        },
  });

  const onSubmit = async (data: TechstarsFormData | OtherFormData) => {
    setIsSubmitting(true);
    
    try {
      const applicationData = {
        company_name: data.companyName,
        contact_email: data.contactEmail,
        accelerator_name: isTechstars ? "Techstars" : (data as OtherFormData).acceleratorName,
        accelerator_type: isTechstars ? "techstars" : "other",
        batch_cohort: data.batchCohort || null,
        company_description: data.companyDescription,
        verification_details: isTechstars ? null : (data as OtherFormData).verificationDetails,
      };

      const { error } = await supabase
        .from("accelerator_applications")
        .insert([applicationData]);

      if (error) {
        console.error("Error submitting application:", error);
        toast.error("Failed to submit application. Please try again.");
        return;
      }

      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
      
      // Reset form after short delay
      setTimeout(() => {
        setIsSubmitted(false);
        form.reset();
        onClose();
      }, 3000);

    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsSubmitted(false);
      form.reset();
      onClose();
    }
  };

  const techstarsBatches = [
    "2024 - Class 1", "2024 - Class 2", "2024 - Class 3",
    "2023 - Class 1", "2023 - Class 2", "2023 - Class 3",
    "2022 - Class 1", "2022 - Class 2", "2022 - Class 3",
    "2021 - Class 1", "2021 - Class 2", "2021 - Class 3",
    "2020 or Earlier"
  ];

  if (isSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Application Submitted!</h3>
            <p className="text-gray-600 mb-4">
              {isTechstars 
                ? "We'll review your Techstars application and get back to you within 24 hours."
                : "We'll review your application and get back to you within 2-3 business days."
              }
            </p>
            <p className="text-sm text-gray-500">
              You'll receive a confirmation email shortly at {form.getValues("contactEmail")}.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isTechstars ? "Techstars Application" : "Accelerator Application"}
          </DialogTitle>
          <DialogDescription>
            {isTechstars
              ? "Apply for free access as a Techstars member. We'll verify your batch information and approve within 24 hours."
              : "Apply for free access from your accelerator program. Please provide verification details for manual review."
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your company name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="founder@company.com" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isTechstars && (
              <FormField
                control={form.control}
                name="acceleratorName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Accelerator Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., Y Combinator, 500 Global, Seedcamp" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="batchCohort"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isTechstars ? "Techstars Batch" : "Batch/Cohort (Optional)"}
                  </FormLabel>
                  <FormControl>
                    {isTechstars ? (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your Techstars batch" />
                        </SelectTrigger>
                        <SelectContent>
                          {techstarsBatches.map((batch) => (
                            <SelectItem key={batch} value={batch}>
                              {batch}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        placeholder="e.g., W23, Summer 2023, Batch 15" 
                        {...field} 
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief description of what your company does and your fundraising stage"
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isTechstars && (
              <FormField
                control={form.control}
                name="verificationDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verification Details</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="How can we verify your accelerator membership? (e.g., demo day presentation link, accelerator email, founder directory listing)"
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Application"
                )}
              </Button>
            </div>
          </form>
        </Form>

        {isTechstars && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-xs text-blue-700">
              <strong>Fast Track:</strong> Techstars applications are typically approved within 24 hours. 
              Make sure to use the email associated with your Techstars batch for faster verification.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AcceleratorApplicationModal;
